import React, { useEffect, useMemo, useState } from 'react';
import { Currency, MurexInstrumentMappingOptionModel, UpdateMurexInstrumentMappingsRequest } from '../../../../Generated/Raven-Demeter';
import SelectInput, { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import ActionModal from '../../../Components/Modals/ActionModal/ActionModal';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './MurexInstrumentMappingPage.module.scss';
import { getCurrencyLabel } from './MurexInstrumentMappingPageDefinitions';

export type MurexInstrumentMapping = Omit<UpdateMurexInstrumentMappingsRequest, 'murexDerivedInstrumentKeys'>;

interface IMurexInstrumentMappingModalProps {
    showModal: boolean;
    mappingOptions: SelectInputOption<MurexInstrumentMappingOptionModel>[];
    currentMappingOption?: MurexInstrumentMappingOptionModel;
    currentCurrency?: Currency;
    currentContractSize?: number;
    handleSave: (mapping: MurexInstrumentMapping) => void;
    handleCancel: () => void;
}

const MurexInstrumentMappingModal: React.FC<IMurexInstrumentMappingModalProps> = (props: IMurexInstrumentMappingModalProps) => {
    const [translations] = useLanguage();
    const [mappingOption, setMappingOption] = useState<MurexInstrumentMappingOptionModel>();
    const [currency, setCurrency] = useState<Currency>();
    const [contractSizeInput, setContractSizeInput] = useState<string>('');

    useEffect(() => {
        if (!props.showModal) {
            return;
        }

        setMappingOption(props.currentMappingOption);
        setCurrency(props.currentCurrency);
        setContractSizeInput(props.currentContractSize === undefined ? '' : `${props.currentContractSize}`);
    }, [props.showModal]);

    const currencyOptions = useMemo<SelectInputOption<Currency | undefined>[]>(
        () => [
            { value: undefined, label: translations.words.none },
            ...Object.values(Currency).map((currencyValue) => ({ value: currencyValue, label: getCurrencyLabel(currencyValue) })),
        ],
        [translations],
    );

    const contractSize = Number(contractSizeInput);
    const hasContractSize = contractSizeInput.trim() !== '' && Number.isFinite(contractSize);

    const handleSave = () => {
        if (!mappingOption) {
            return;
        }

        props.handleSave({
            exchange: mappingOption.exchange,
            subExchange: mappingOption.subExchange,
            commodity: mappingOption.commodity,
            currency,
            contractSize: hasContractSize ? contractSize : null,
        });
    };

    return (
        <ActionModal
            header={translations.murexInstrumentMapping.text.editMappingHeader}
            showModal={props.showModal}
            actionButtonName={translations.actions.save}
            actionButtonDisabled={!mappingOption}
            handleAction={handleSave}
            handleCancel={props.handleCancel}
            track={{ eventCategory: 'MurexInstrumentMapping', eventAction: 'EditMappingModal' }}
        >
            <div className={styles.murex_instrument_mapping_modal_inputs}>
                <SelectInput
                    title={translations.marketIndicatorsManagement.headers.exchangeAndCommodity}
                    value={mappingOption}
                    options={props.mappingOptions}
                    isSearchable
                    handleOptionChange={setMappingOption}
                    track={{ eventCategory: 'MurexInstrumentMapping', eventAction: 'MappingCommodityChanged' }}
                />
                <SelectInput
                    title={translations.words.currency}
                    value={currency}
                    options={currencyOptions}
                    isSearchable
                    handleOptionChange={setCurrency}
                    track={{ eventCategory: 'MurexInstrumentMapping', eventAction: 'MappingCurrencyChanged' }}
                />
                <TextInput
                    type="number"
                    title={translations.murexInstrumentMapping.fields.contractSize}
                    value={contractSizeInput}
                    handleTextChange={setContractSizeInput}
                    track={{ eventCategory: 'MurexInstrumentMapping', eventAction: 'MappingContractSizeChanged' }}
                />
            </div>
        </ActionModal>
    );
};

export default MurexInstrumentMappingModal;
